import {
  useContext,
  createContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import axios from "axios";
import { toast } from "react-toastify";

const initialAuthState = {
  isAdmin: null,
  isFrontDesk: null,
  isLoggedIn: null,
  user: null,
  token: null,
  error: null,
  loading: false,
  success: false,
  isFrontDesk: false,
};
const AuthContext = createContext(initialAuthState);

export const AuthContextProvider = ({ children }) => {
  const [authState, setAuthState] = useState(initialAuthState);
  // let baseUrl = "http://localhost:3004";
  const baseUrl =
    process.env.REACT_APP_DEVELOPMENT_MODE === "DEVELOPMENT"
      ? process.env.REACT_APP_TESTING_REACT_APP_URL
      : process.env.REACT_APP_URL;

  const login = async (credentials) => {
    setAuthState((prevData) => ({ ...prevData, loading: true, error: null }));
    try {
      const { data } = await axios.post(`${baseUrl}/auth/login`, credentials, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      if (data.success) {
        const { token } = data;
        localStorage.setItem("token", token);
        loadUser();
        toast.success("Logged In Successfully");
      }
    } catch (error) {
      setAuthState((prevData) => ({
        ...prevData,
        loading: false,
        error: error.response,
        success: false,
      }));
      if (error?.response?.status >= 400 && error?.response?.status < 500) {
        return toast.warning(error?.response?.data?.data?.error);
      }
      toast.error("Something Went Wrong");
    }
  };

  const forgotPassword = async (email) => {
    setAuthState((prevData) => ({ ...prevData, loading: true, error: null }));
    try {
      const { data } = await axios.post(
        `${baseUrl}/user/forgot-password`,
        email,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      if (data.success) {
        toast.success("Email sent with reset link");
        setAuthState((prevData) => ({
          ...prevData,
          loading: false,
        }));
      }
    } catch (error) {
      setAuthState((prevData) => ({
        ...prevData,
        loading: false,
        error: error.response,
        success: false,
      }));
      if (error?.response?.status > 400 && error?.response?.status < 500) {
        return toast.warning(error.response.data.data.error);
      }
      return toast.error("Something Went Wrong");
    }
  };

  const resetPassword = async (password, token) => {
    setAuthState((prevData) => ({ ...prevData, loading: true, error: null }));
    try {
      const { data } = await axios.put(
        `${baseUrl}/user/reset-password/${token}`,
        password,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      if (data.success) {
        toast.success("Password has Been Reset, Please Login");
        setAuthState((prevData) => ({
          ...prevData,
          loading: false,
        }));
      }
    } catch (error) {
      setAuthState((prevData) => ({
        ...prevData,
        loading: false,
        error: error.response,
        success: false,
      }));
      if (error?.response?.status >= 400 && error?.response?.status <= 500) {
        return toast.warning(error.response.data.data.error);
      }
      return toast.error("Something Went Wrong");
    }
  };

  const register = async (credentials) => {
    setAuthState((prevData) => ({ ...prevData, loading: true, error: null }));
    try {
      const { data } = await axios.post(
        `${baseUrl}/auth/register`,
        credentials,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      const { token } = data;
      if (data.success) {
        localStorage.setItem("token", token);
        loadUser();
        toast.success("Registered Successfully");
      }
    } catch (error) {
      setAuthState((prevData) => ({
        ...prevData,
        loading: false,
        error: error,
        success: false,
      }));
      if (error?.response?.status >= 400 && error?.response?.status < 500) {
        return toast.warning(error?.response?.data?.data?.error);
      }
      toast.error("Something Went Wrong");
    }
  };

  const logout = async () => {
    try {
      localStorage.removeItem("token");
      setAuthState({
        isLoggedIn: false,
        user: null,
        token: null,
      });
      loadUser();
    } catch {
      setAuthState((prevData) => ({
        ...prevData,
        loading: false,
        error: true,
      }));
    }
  };

  const loadUser = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        setAuthState((prevData) => ({
          ...prevData,
          loading: true,
          token: token,
          error: null,
        }));
        const { data } = await axios.get(`${baseUrl}/auth/profile`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (data.success) {
          setAuthState((prevData) => ({
            ...prevData,
            isAdmin: data.data?.isAdmin,
            isLoggedIn: true,
            user: data.data,
            token: token,
            success: true,
            loading: false,
            isFrontDesk: data.data?.userRole === "frontdesk" ? true : false,
          }));
        }
      } catch (error) {
        setAuthState((prevData) => ({
          ...prevData,
          error: null,
          isLoggedIn: false,
          success: false,
          loading: false,
        }));
      }
    } else {
      setAuthState((prevData) => ({
        ...prevData,
        loading: false,
        isLoggedIn: false,
        success: false,
      }));
    }
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  return (
    <AuthContext.Provider
      value={{
        login,
        logout,
        register,
        loadUser,
        forgotPassword,
        resetPassword,
        ...authState,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => useContext(AuthContext);
